import { useEffect, useState } from 'react';

import { useKioskSession } from '@/lib/kioskSession';
import type { AssociateStatusOutcome } from '@/services/associateStatus';
import { fetchAssociateStatus } from '@/services/associateStatus';

/** How the read answered, as the screen needs it — `undefined` while it has not. */
export type AssociateStatusAnswer = AssociateStatusOutcome['kind'];

export interface AssociateStatusRead {
  readonly loading: boolean;
  /**
   * ⭐ **What the read answered, so the screen can tell `none` from `unreachable`** [03-us-kas-1a § Where it fits].
   * The two used to collapse into one no-card state; now an empty answer asks whether to apply for a new job and
   * a failed one hands off to a recruiter. `undefined` when no read was made (cards already in the session, or no
   * `candidateId`) or it has not answered yet.
   */
  readonly answer: AssociateStatusAnswer | undefined;
}

/**
 * Loads the status card once, when the screen appears [KAS-1].
 *
 * **Once, and never again.** No polling and no refetch: the card describes an assignment, not a live feed,
 * and a candidate standing at a kiosk for two minutes does not need it re-read. A poll here would also turn
 * one person's visit into a stream of Bullhorn reads on a shared credential.
 *
 * It loads only when there is nothing in the session already, so arriving from KID-5's *reconfirm* option
 * with the card in hand does not fetch it a second time.
 *
 * ⭐ **The answer is held HERE, not in the session** [03-us-kas-1a Scenario 13]. An empty answer stores no cards,
 * so a second tap on *See what I applied for* mounts this hook afresh and reads again — and asks again. Keeping
 * `none` on the store would turn that second tap into a cached answer the candidate cannot refresh.
 */
export function useAssociateStatus(): AssociateStatusRead {
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const cards = useKioskSession((state) => state.associateCards);
  const setAssociateCards = useKioskSession((state) => state.setAssociateCards);
  /*
   * ONE state for "settled" and "what it said", never two: the screen picks its branch off this value, and a
   * render that saw the read settled before it saw the answer would paint the hand-off for one frame on an empty
   * answer — the frame Scenario 3 forbids. Loading is DERIVED from what is actually true (somebody to look up,
   * no card yet, no answer), so nothing has to be set before the request is even made.
   */
  const [answer, setAnswer] = useState<AssociateStatusAnswer | undefined>(undefined);

  useEffect(() => {
    if (cards !== undefined || candidateId === undefined) return;

    let live = true;

    void fetchAssociateStatus(candidateId).then((outcome) => {
      // The screen can unmount mid-flight — the idle timer clears the session and returns to Welcome — and
      // writing a card into a session that has just ended would carry one candidate's assignment into the
      // next person's visit.
      if (!live) return;
      if (outcome.kind === 'cards') setAssociateCards(outcome.cards);
      setAnswer(outcome.kind);
    });

    return () => {
      live = false;
    };
    // `cards` is deliberately absent: including it would re-run the effect the moment the card lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, setAssociateCards]);

  return {
    loading: candidateId !== undefined && cards === undefined && answer === undefined,
    answer,
  };
}
